import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Custom hook to fetch and manage roles from roleserver
 *
 * @returns {Object} - { roles, loading, error, refetch }
 */
export const useRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRoles = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('/api/roles');
      const rolesData = response.data?.roles || [];
      setRoles(rolesData);
      return rolesData;
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setError(err.message || 'Failed to fetch roles');
      setRoles([]);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  return {
    roles,
    loading,
    error,
    refetch: fetchRoles,
  };
};

/**
 * Custom hook to fetch a specific role's menu data
 *
 * @param {string} roleKey - The role key to fetch menu for
 * @returns {Object} - { roleMenu, loading, error, refetch }
 */
export const useRoleMenu = (roleKey) => {
  const [roleMenu, setRoleMenu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRoleMenu = async () => {
    if (!roleKey) {
      setRoleMenu(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/menu/${roleKey}`);
      setRoleMenu(response.data);
      return response.data;
    } catch (err) {
      console.error(`Failed to fetch menu for role ${roleKey}:`, err);
      setError(err.message || 'Failed to fetch role menu');
      setRoleMenu(null);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoleMenu();
  }, [roleKey]);

  return {
    roleMenu,
    loading,
    error,
    refetch: fetchRoleMenu,
  };
};
